// GtOutreach.js — Auto outreach (C1-OUTREACH, 3 Nov 2026; tables 4 Nov 2026).
//
// c84: hiring signals into client outreach. Hiring now (the weekly check), People (who to write to, and the
// companies), Sent and Not sent. Every list is a table like Candidates and Clients: searched, filtered and paged
// on the server, so it stays quick at thousands of people and emails. A row opens a panel on the right with
// the detail and the actions; ticked rows get actions in a bar above the table.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthFetch } from './GtLexaAdmin';
import GtTable from '../../components/GtTable';
import GtPager from '../../components/GtPager';
import GtEditor from '../../components/GtEditor';

const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);
const host = (u) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch (e) { return ''; } };
function ukDay(v) {
  if (!v) return '';
  try { return new Date(v).toLocaleDateString('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short' }); } catch (e) { return ''; }
}
function when(v) { try { return v ? new Date(v).toLocaleString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''; } catch (e) { return ''; } }
const bodyText = (t) => String(t || '').replace(/\[\[([^\]]+)\]\]\{\{(article|library)\}\}/g, '$1');
// The email body is kept as plain paragraphs with [[words]]{{article}} links (what the email sends). These turn it
// into the platform editor's HTML and back. The two links show as links named Article and Library page.
const escH = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const LINK_HREF = { article: 'https://gtco-link/article', library: 'https://gtco-link/library' };
function bodyToHtml(t) {
  return String(t || '').split(/\n{2,}/).map((para) => {
    let h = ''; let last = 0;
    para.replace(/\[\[([^\]]+)\]\]\{\{(article|library)\}\}/g, (m0, w, k, at) => { h += escH(para.slice(last, at)) + '<a href="' + LINK_HREF[k] + '">' + escH(w) + '</a>'; last = at + m0.length; return m0; });
    h += escH(para.slice(last));
    return '<p>' + h.replace(/\n/g, '<br>') + '</p>';
  }).join('');
}
function htmlToBody(html) {
  const doc = new DOMParser().parseFromString('<div>' + (html || '') + '</div>', 'text/html');
  const blocks = [];
  let cur = '';
  const flush = () => { if (cur.trim()) blocks.push(cur.replace(/[ \t]+/g, ' ').replace(/ *\n */g, '\n').trim()); cur = ''; };
  const walk = (n) => {
    if (n.nodeType === 3) { cur += n.nodeValue.replace(/\s+/g, ' '); return; }
    if (n.nodeType !== 1) return;
    const tag = n.tagName;
    if (tag === 'BR') { cur += '\n'; return; }
    if (tag === 'A') {
      const href = n.getAttribute('href') || ''; const words = n.textContent.replace(/\s+/g, ' ').trim();
      const k = Object.keys(LINK_HREF).find((x) => href === LINK_HREF[x]);
      if (k && words) cur += '[[' + words.replace(/[[\]]/g, '') + ']]{{' + k + '}}';
      else cur += words + (href && /^https?:/.test(href) && href !== words ? ' (' + href + ')' : '');
      return;
    }
    const block = /^(P|DIV|H[1-6]|LI|BLOCKQUOTE|UL|OL|TABLE|TR)$/.test(tag);
    if (block) flush();
    if (tag === 'LI') cur += '- ';
    n.childNodes.forEach(walk);
    if (block) flush();
  };
  doc.body.firstChild.childNodes.forEach(walk);
  flush();
  return blocks.join('\n\n');
}
// What the email check found, as a pill. The check runs as soon as people are found.
const CHECK_PILL = { deliverable: ['Exists', 'green'], catch_all: ['Catch-all', 'amber'], shared_inbox: ['Shared inbox', 'amber'], unknown: ['Could not check', 'amber'],
  bad_email: ['Bad email', 'rose'], will_not_deliver: ['Will not deliver', 'rose'], no_mail_server: ['Will not deliver', 'rose'], disposable: ['Throwaway', 'rose'], spamtrap: ['Spam trap', 'rose'] };
const CheckPill = ({ kind, email }) => { if (!email) return <span className="gtt-dash">—</span>; const [t, tone] = CHECK_PILL[kind] || ['Not checked yet', 'slate']; return <Pill tone={tone}>{t}</Pill>; };
const Pill = ({ tone, children, title }) => <span className={'gtt-pill gtt-p-' + (tone || 'slate')} title={title}>{children}</span>;
const Dash = () => <span className="gtt-dash">—</span>;
const Two = ({ a, b, onClick }) => (
  <div className="gtob2-two">
    {onClick ? <button type="button" className="gtt-nm gtt-nm-link" onClick={onClick}>{a}</button> : <div className="gtt-nm">{a}</div>}
    {b ? <div className="gtt-sub">{b}</div> : null}
  </div>
);

// ---------------------------------------------------------------- shared pieces
// A paged list from the server. Changing the filter or search goes back to page 1.
function useList(authFetch, url, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const key = url + '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v !== '' && v != null)).toString();
  const seq = useRef(0);
  const load = useCallback(() => {
    const n = ++seq.current;
    setLoading(true);
    return authFetch(key).then((j) => { if (n === seq.current) { setData(j); setErr(''); } }).catch((e) => { if (n === seq.current) setErr(e.message); })
      .finally(() => { if (n === seq.current) setLoading(false); });
  }, [authFetch, key]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, err, reload: load };
}
function useDebounced(v, ms = 250) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}
// Search box plus filter chips with counts.
function Toolbar({ q, setQ, placeholder, chips, f, setF, right }) {
  return (
    <div className="gtob2-tools">
      {chips && (
        <div className="gtob2-chips" role="tablist">
          {chips.map((c) => (
            <button key={c.key} type="button" role="tab" aria-selected={f === c.key} className={'gtob2-chip' + (f === c.key ? ' gtob2-chip--on' : '') + (c.warn && c.n ? ' gtob2-chip--warn' : '')} onClick={() => setF(c.key)}>
              {c.label}{c.n != null && <b>{c.n}</b>}
            </button>
          ))}
        </div>
      )}
      <div className="gtob2-tools-r">
        <div className="gtc-tb-search gtob2-search">
          <span className="gtc-tb-searchicon">⌕</span>
          <input type="search" placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} aria-label={placeholder} />
        </div>
        {right}
      </div>
    </div>
  );
}
// Shown above the table while rows are ticked.
function BulkBar({ n, onClear, children }) {
  if (!n) return null;
  return (
    <div className="gtob2-bulk" role="region" aria-label="Selected rows">
      <span><b>{n}</b> selected</span>
      <div className="gtob2-bulk-a">{children}<button type="button" className="gtiv-link" onClick={onClear}>Clear selection</button></div>
    </div>
  );
}
function Drawer({ kicker, title, onClose, children, foot, wide }) {
  useEffect(() => { const k = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  return (
    <div className="gtbx-scrim" onClick={onClose}>
      <aside className={'gtbx-drawer gtob2-drawer' + (wide ? ' gtob2-drawer--wide' : '')} role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="gtbx-drawer-head">
          <div>{kicker && <div className="gtbx-kicker">{kicker}</div>}<h2>{title}</h2></div>
          <button type="button" className="gtbx-x" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="gtbx-drawer-body">{children}</div>
        {foot && <div className="gtob2-foot">{foot}</div>}
      </aside>
    </div>
  );
}
const Field = ({ label, children }) => <div className="gtob2-field"><span>{label}</span><div>{children}</div></div>;
function useSelection(rows) {
  const [sel, setSel] = useState(() => new Set());
  const toggle = (id) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSel((p) => { const ids = (rows || []).map((r) => r.id); const all = ids.length && ids.every((i) => p.has(i)); const n = new Set(p); ids.forEach((i) => (all ? n.delete(i) : n.add(i))); return n; });
  // Only rows on screen stay ticked: a new page, search or filter drops the rest, so a bulk action never
  // reaches rows you cannot see.
  const key = (rows || []).map((r) => r.id).join(',');
  useEffect(() => { setSel((p) => { const on = new Set((rows || []).map((r) => r.id)); const n = new Set(Array.from(p).filter((i) => on.has(i))); return n.size === p.size ? p : n; }); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { sel, setSel, toggle, toggleAll, clear: () => setSel(new Set()) };
}

// ---------------------------------------------------------------- Hiring now
function HiringNow({ authFetch, navigate }) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const load = useCallback(() => authFetch('/api/insights/admin/hiring-check').then(setD).catch((e) => setMsg(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  const latest = d && d.runs[0];
  const running = !!(d && (d.running || (latest && latest.status === 'running')));
  useEffect(() => { if (!running) return undefined; const t = setInterval(load, 5000); return () => clearInterval(t); }, [running, load]);
  useEffect(() => { setPage(0); }, [q]);
  const rows = useMemo(() => {
    if (!d) return [];
    const t = q.trim().toLowerCase();
    const all = d.hits.map((h) => ({ ...h, id: h.id }));
    return t ? all.filter((h) => ((h.client_name || h.company) + ' ' + h.role_title + ' ' + (h.posting_title || '') + ' ' + (h.location || '')).toLowerCase().includes(t)) : all;
  }, [d, q]);
  const pageRows = rows.slice(page * size, page * size + size);
  const hs = useSelection(pageRows);
  const [done, setDone] = useState('');
  const [hbusy, setHbusy] = useState(false);
  async function dismissHits() {
    const ids = Array.from(new Set(pageRows.filter((h) => hs.sel.has(h.id) && h.client_id).map((h) => h.client_id)));
    if (!ids.length) { setDone('None of these have a client yet, so there is nothing to dismiss.'); return; }
    setHbusy(true); setMsg(''); setDone('');
    try {
      await authFetch('/api/outreach/people/companies/dismiss', { method: 'POST', body: JSON.stringify({ ids }) });
      setDone(plural(ids.length, 'company', 'companies') + ' dismissed: nobody there is looked up or emailed. Restore them under People, Companies, Dismissed.');
      hs.clear();
    } catch (e) { setMsg(e.message); }
    setHbusy(false);
  }
  async function run() {
    setMsg('');
    try { await authFetch('/api/insights/admin/hiring-check/run', { method: 'POST' }); await load(); } catch (e) { setMsg(e.message); }
  }
  if (!d) return msg ? <div className="gtiv-card gtiv-err">{msg}</div> : <div className="gtiv-card gtiv-loading">Loading…</div>;
  const last = d.runs.find((r) => r.status === 'done');
  const shown = d.found_run || d.runs.find((r) => r.id === d.last_id);
  const cols = [
    { key: 'company', label: 'Company', width: 190, cell: (h) => <Two a={h.client_name || h.company} b={h.location} onClick={h.client_id ? () => navigate('/clients/' + h.client_id) : null} /> },
    { key: 'role', label: 'Hiring for', width: 180, cell: (h) => <span className="gtt-ctext">{h.role_title}</span> },
    { key: 'advert', label: 'Advert', width: 200, cell: (h) => <a className="gtt-link" href={h.posting_url} target="_blank" rel="noopener noreferrer" title={h.posting_url}>{h.posting_title || host(h.posting_url)}</a> },
    { key: 'named', label: 'Named in the advert', width: 150, cell: (h) => (h.named_contact && h.named_contact.name ? <Two a={h.named_contact.name} b={h.named_contact.title} /> : <Dash />) },
    { key: 'new', label: 'Client', width: 100, cell: (h) => (h.client_new ? <Pill tone="green">New prospect</Pill> : <Pill>Existing</Pill>) },
    { key: 'found', label: 'Found', width: 90, cell: (h) => <span className="gtt-date">{ukDay(h.created_at || h.published_at)}</span> },
  ];
  return (
    <>
      <section className="gtob-hero">
        <div className="gtob-hero-t">
          <b>Hiring check</b>
          <span>Every Monday at 07:00, each role with a live Insights article is searched for job adverts from the last 7 days. Each company hiring for it becomes a prospect client, with the advert saved on the client. The People tab then finds who to write to.</span>
        </div>
        <button type="button" className="gt-btn gt-btn--primary" disabled={running || !d.live_roles} title={!d.live_roles ? 'No articles are live yet' : ''} onClick={run}>{running ? 'Checking…' : 'Run the check now'}</button>
      </section>
      {running && <div className="gtiv-card gtob-prog"><div className="gtjf-prog"><div className="gtjf-bar"><i style={{ width: (latest && latest.roles ? Math.max(5, Math.round((latest.roles_done / latest.roles) * 100)) : 5) + '%' }} /></div><span>Checking {latest ? latest.roles_done : 0} of {latest ? latest.roles : '…'} roles. You can leave this screen.</span></div></div>}
      {!running && latest && latest.status === 'failed' && <div className="gtiv-card gtiv-err">The last check did not finish: {latest.error}</div>}
      {!running && last && last.error && <div className="gtiv-card gtob-warn">{last.error}</div>}
      {msg && <div className="gtiv-card gtiv-err">{msg}</div>}
      <div className="gtob-stats">
        <div className="gtob-stat"><em>Roles with a live article</em><b>{d.live_roles}</b></div>
        <div className="gtob-stat"><em>Adverts found</em><b>{shown ? shown.postings : 0}</b></div>
        <div className="gtob-stat"><em>Companies hiring</em><b>{shown ? shown.companies : 0}</b></div>
        <div className="gtob-stat gtob-stat--g"><em>New prospects</em><b>{shown ? shown.new_clients : 0}</b></div>
      </div>
      <p className="gtob-when">{last ? <>Last check {ukDay(last.finished_at || last.started_at)}{last.id !== d.last_id && d.found_at ? ', nothing new. Showing what was found ' + ukDay(d.found_at) + '.' : '.'}</> : d.live_roles ? 'Not run yet. The first check runs next Monday, or run it now.' : 'No articles are live yet. The check starts once one is.'}</p>
      <Toolbar q={q} setQ={setQ} placeholder="Search companies, roles and places" />
      {done && <div className="gtiv-card gtob2-done"><span>{done}</span><button type="button" className="gt-btn" onClick={() => setDone('')}>OK</button></div>}
      <BulkBar n={hs.sel.size} onClear={hs.clear}>
        <button type="button" className="gt-btn gtob2-danger" disabled={hbusy} onClick={dismissHits}>Dismiss {plural(new Set(pageRows.filter((h) => hs.sel.has(h.id) && h.client_id).map((h) => h.client_id)).size, 'company', 'companies')}</button>
      </BulkBar>
      <GtTable columns={cols} rows={pageRows} loading={false} selectedIds={hs.sel} onToggle={hs.toggle} onToggleAll={hs.toggleAll} emptyText={q ? 'No adverts match.' : 'No adverts found yet.'} />
      <GtPager page={page} pageSize={size} total={rows.length} onPage={setPage} onPageSize={(n) => { setSize(n); setPage(0); }} />
    </>
  );
}

// ---------------------------------------------------------------- People
const SOURCE = { advert: 'Named in the advert', gtco: 'Already in GTCO', sonar: 'Sonar', apollo: 'Apollo', web: 'Found on the web' };
// Ready means it can still be emailed: sendable and nothing written, sent or stopped yet.
function personState(p) {
  if (p.send_status === 'replied') return ['Replied', 'green'];
  if (p.send_status === 'done') return ['Emailed', 'blue'];
  if (p.send_status === 'in_progress' && !p.has_sent) return p.pending === 'needs_you' ? ['Waiting for you to read', 'amber'] : p.pending === 'failed' ? ['Send failed', 'rose'] : ['Queued to go', 'teal'];
  if (p.send_status === 'in_progress') return ['Emailed, follow-up due', 'blue'];
  if (p.send_status === 'bounced') return ['Bounced', 'rose'];
  if (p.send_status === 'unsubscribed') return ['Unsubscribed', 'amber'];
  if (p.send_status === 'stopped') return [p.stop_reason || 'Stopped', 'amber'];
  return p.sendable ? ['Ready', 'green'] : [p.reason || 'Not emailed', 'amber'];
}
const COMPANY_STATE = { check: ['Check website', 'amber'], waiting: ['Waiting to be looked at', 'teal'], ready: ['Ready', 'green'], replied: ['Someone replied', 'green'], done: ['Looked at', 'slate'], dismissed: ['Dismissed', 'slate'] };

function NeverList({ authFetch }) {
  const [list, setList] = useState(null);
  const [add, setAdd] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => { authFetch('/api/outreach/never').then((j) => setList(j.domains)).catch((e) => setMsg(e.message)); }, [authFetch]);
  async function save(next) {
    setMsg('');
    try { setList((await authFetch('/api/outreach/never', { method: 'PUT', body: JSON.stringify({ domains: next }) })).domains); setAdd(''); } catch (e) { setMsg(e.message); }
  }
  if (!list) return msg ? <div className="gtiv-card gtiv-err">{msg}</div> : <div className="gtbx-empty">Loading…</div>;
  return (
    <div className="gtob-never-in">
      <p className="gtob2-note">Nobody at these companies is looked up or emailed. Type a website, or anyone's email address there.</p>
      <form className="gtob-never-f" onSubmit={(e) => { e.preventDefault(); if (add.trim()) save(list.concat(add.trim())); }}>
        <input className="gtiv-in" value={add} placeholder="company.com" onChange={(e) => setAdd(e.target.value)} aria-label="Add a company website" />
        <button type="submit" className="gt-btn" disabled={!add.trim()}>Add</button>
      </form>
      {msg && <p className="gtob-never-e">{msg}</p>}
      <div className="gtob-never-l">
        {list.map((x) => <span key={x} className="gtob-tag">{x}<button type="button" aria-label={'Remove ' + x} onClick={() => save(list.filter((y) => y !== x))}>×</button></span>)}
      </div>
    </div>
  );
}

function People({ authFetch, navigate }) {
  const [view, setView] = useState('people'); // people | companies
  const [f, setF] = useState('all');
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim());
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [open, setOpen] = useState(null);     // a person or a company row
  const [never, setNever] = useState(false);
  const [msg, setMsg] = useState('');
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);
  const [ov, setOv] = useState(null);
  const loadOv = useCallback(() => authFetch('/api/outreach/people').then(setOv).catch((e) => setMsg(e.message)), [authFetch]);
  useEffect(() => { loadOv(); }, [loadOv]);
  const latest = ov && ov.runs[0];
  const running = !!(ov && (ov.running || (latest && latest.status === 'running')));
  const list = useList(authFetch, '/api/outreach/people/list', { view, f, q: dq, page, size });
  const pc = useList(authFetch, '/api/outreach/people/list', { view: 'people', size: 1 });     // figures for the cards
  const cc = useList(authFetch, '/api/outreach/people/list', { view: 'companies', size: 1 });
  const reloadAll = useCallback(() => { list.reload(); pc.reload(); cc.reload(); loadOv(); }, [list, pc, cc, loadOv]);
  useEffect(() => { if (!running) return undefined; const t = setInterval(reloadAll, 5000); return () => clearInterval(t); }, [running, reloadAll]);
  useEffect(() => { setPage(0); }, [view, f, dq, size]);
  const rows = (list.data && list.data.rows) || [];
  const s = useSelection(rows);
  useEffect(() => { s.clear(); }, [view, f]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = (v, filter) => { setView(v); setF(filter); setQ(''); };
  async function run() {
    setMsg('');
    try { await authFetch('/api/outreach/people/run', { method: 'POST' }); await loadOv(); } catch (e) { setMsg(e.message); }
  }
  async function companies(ids, restore) {
    setBusy(true); setMsg('');
    try { await authFetch('/api/outreach/people/companies/dismiss', { method: 'POST', body: JSON.stringify({ ids, restore: !!restore }) }); s.clear(); setOpen(null); reloadAll(); } catch (e) { setMsg(e.message); }
    setBusy(false);
  }
  // Not their website: the AI looks for the right one and puts it on the client.
  async function findSite(ids) {
    setBusy(true); setMsg(''); setDone('');
    try {
      const r = await authFetch('/api/outreach/people/companies/find-site', { method: 'POST', body: JSON.stringify({ ids }) });
      const res = r.results || [];
      const ok = res.filter((x) => x.confirmed).length; const check = res.filter((x) => x.website && !x.confirmed).length; const none = res.filter((x) => !x.website).length;
      const one = res.length === 1 ? res[0] : null;
      setDone(one ? (one.confirmed ? one.name + ': website changed to ' + one.website + '. People there are looked up on it next.'
        : one.website ? one.name + ': the AI suggests ' + one.website + '. It does not carry the name, so check it and confirm.'
        : 'No other website found for ' + one.name + '. Add it on the client, or dismiss the company.')
        : [ok && plural(ok, 'website', 'websites') + ' found and changed', check && check + ' to check', none && none + ' not found'].filter(Boolean).join(', ') + '.');
      s.clear(); setOpen(null); reloadAll();
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  }
  const sendOv = useSending(authFetch);
  const checkingNow = !!(sendOv.d && sendOv.d.check && sendOv.d.check.running);
  useEffect(() => { if (!checkingNow) return undefined; const t = setInterval(() => { sendOv.load(); reloadAll(); }, 3000); return () => clearInterval(t); }, [checkingNow]); // eslint-disable-line react-hooks/exhaustive-deps
  async function checkEmails() {
    setMsg('');
    try { await authFetch('/api/outreach/sending/check-emails', { method: 'POST' }); await sendOv.load(); } catch (e) { setMsg(e.message); }
  }
  async function people(action) {
    const ids = rows.filter((r) => s.sel.has(r.id)).map((r) => r.id);
    if (!ids.length) return;
    setBusy(true); setMsg(''); setDone('');
    try {
      const r = await authFetch('/api/outreach/people/bulk', { method: 'POST', body: JSON.stringify({ ids, action }) });
      const what = action === 'stop' ? 'taken off the send list or their follow-up stopped' : action === 'restore' ? 'put back on the send list (the rules still apply)' : 'cleared from the lists';
      setDone(plural(r.done, 'person', 'people') + ' ' + what + (r.done < r.asked ? '. ' + (r.asked - r.done) + ' could not be: already replied, bounced, unsubscribed or finished.' : '.'));
      s.clear(); reloadAll();
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  }
  async function siteOk(id) {
    setBusy(true); setMsg('');
    try { await authFetch('/api/outreach/people/company/' + id + '/site-ok', { method: 'POST' }); setOpen(null); reloadAll(); } catch (e) { setMsg(e.message); }
    setBusy(false);
  }
  const pcs = (pc.data && pc.data.counts) || {};
  const ccs = (cc.data && cc.data.counts) || {};
  const counts = (list.data && list.data.counts) || {};
  const peopleCols = [
    { key: 'person', label: 'Person', width: 220, cell: (p) => <Two a={p.full_name} b={p.title || 'Title not known'} /> },
    { key: 'company', label: 'Company', width: 180, cell: (p) => <Two a={p.company} b={p.role_title} onClick={() => navigate('/clients/' + p.client_id)} /> },
    { key: 'email', label: 'Work email', width: 195, cell: (p) => (p.email ? <span className="gtt-ctext">{p.email}</span> : <Dash />) },
    { key: 'check', label: 'Email check', width: 130, cell: (p) => <CheckPill kind={p.check_kind} email={p.email} /> },
    { key: 'source', label: 'Found by', width: 130, cell: (p) => <span className="gtt-ctext">{SOURCE[p.source] || p.source}</span> },
    { key: 'status', label: 'Status', width: 200, cell: (p) => { const [t, tone] = personState(p); return <Pill tone={tone} title={t}>{t}</Pill>; } },
  ];
  const companyCols = [
    { key: 'company', label: 'Company', width: 200, cell: (c) => <Two a={c.name} b={c.role_title ? 'Hiring for ' + c.role_title : null} /> },
    { key: 'site', label: 'Website', width: 130, cell: (c) => (c.website ? <a className="gtt-link" href={siteUrl(c.website)} target="_blank" rel="noopener noreferrer" title="Open the website">{host(c.website) || c.website} ↗</a> : <Dash />) },
    { key: 'people', label: 'People', width: 60, cell: (c) => <span className="gtt-ctext">{c.people || 0}</span> },
    { key: 'ready', label: 'Ready', width: 60, cell: (c) => (c.ready ? <Pill tone="green">{c.ready}</Pill> : <Dash />) },
    { key: 'emailed', label: 'Emailed', width: 70, cell: (c) => (c.emailed ? <Pill tone="blue">{c.emailed}</Pill> : <Dash />) },
    { key: 'state', label: 'Status', width: 150, cell: (c) => { const [t, tone] = COMPANY_STATE[c.state] || ['', 'slate']; return <Pill tone={tone}>{t}</Pill>; } },
    { key: 'looked', label: 'Looked at', width: 90, cell: (c) => (c.looked_at ? <span className="gtt-date">{ukDay(c.looked_at)}</span> : <Dash />) },
    { key: 'advert', label: 'Advert', width: 60, cell: (c) => (c.posting_url ? <a className="gtt-link" href={c.posting_url} target="_blank" rel="noopener noreferrer">Open</a> : <Dash />) },
  ];
  const peopleChips = [{ key: 'all', label: 'All', n: counts.all }, { key: 'ready', label: 'Ready', n: counts.ready }, { key: 'waiting', label: 'Waiting for you or queued', n: counts.waiting, warn: true }, { key: 'emailed', label: 'Emailed', n: counts.emailed }, { key: 'replied', label: 'Replied', n: counts.replied }, { key: 'not', label: 'Not emailed', n: counts.not },
    { key: 'catch_all', label: 'Catch-all', n: counts.catch_all }, { key: 'shared_inbox', label: 'Shared inbox', n: counts.shared_inbox }, { key: 'could_not_check', label: 'Could not check', n: counts.could_not_check }, { key: 'unchecked', label: 'Not checked yet', n: counts.unchecked }];
  const companyChips = [{ key: 'all', label: 'All', n: counts.all }, { key: 'check', label: 'Check website', n: counts.check, warn: true }, { key: 'ready', label: 'Ready', n: counts.ready }, { key: 'waiting', label: 'Waiting', n: counts.waiting }, { key: 'replied', label: 'Replied', n: counts.replied }, { key: 'done', label: 'Looked at', n: counts.done }, { key: 'dismissed', label: 'Dismissed', n: counts.dismissed }];
  const card = (label, n, on, onClick, tone) => (
    <button type="button" className={'gtob-stat gtob-stat--go' + (tone ? ' gtob-stat--' + tone : '') + (on ? ' gtob-stat--on' : '')} aria-pressed={on} onClick={onClick}><em>{label}</em><b>{n == null ? '…' : n}</b></button>);
  return (
    <>
      <section className="gtob-hero">
        <div className="gtob-hero-t">
          <b>Who to send to</b>
          <span>Up to 3 people at every company hiring: anyone the advert names, then people already in GTCO, then leadership through Apollo. Technical leadership (CTO, heads and directors of engineering or AI) at any size; founders under 100 people; talent leadership over 50. Engineering managers only when the advert names them. Verified work emails on the company's own domain only.</span>
        </div>
        <div className="gtob2-hero-a">
          <button type="button" className="gt-btn" onClick={() => setNever(true)}>Never contact</button>
          <button type="button" className="gt-btn" disabled={!sendOv.d || !sendOv.d.check || sendOv.d.check.running || !sendOv.d.check.pending || !sendOv.d.check.set_up} title={sendOv.d && sendOv.d.check && !sendOv.d.check.set_up ? 'Add BOUNCIFY_API_KEY in Railway' : 'Checks every email address not checked yet'} onClick={checkEmails}>
            {!sendOv.d || !sendOv.d.check ? 'Check emails' : sendOv.d.check.running && sendOv.d.check.run ? 'Checking ' + sendOv.d.check.run.done + ' of ' + sendOv.d.check.run.total : sendOv.d.check.pending ? 'Check emails (' + sendOv.d.check.pending + ')' : 'All emails checked'}</button>
          <button type="button" className="gt-btn gt-btn--primary" disabled={running || !(ov && ov.waiting)} onClick={run}>{running ? 'Looking…' : ov && ov.waiting ? (ov.waiting > 40 ? 'Find people now (next 40 of ' + ov.waiting + ')' : 'Find people now (' + ov.waiting + ')') : 'Nothing waiting'}</button>
        </div>
      </section>
      {ov && !ov.apollo && <div className="gtiv-card gtob-warn">Apollo is not set up, so only people named in adverts or already in GTCO can be found. Add APOLLO_API_KEY in Railway.</div>}
      {running && <div className="gtiv-card gtob-prog"><div className="gtjf-prog"><div className="gtjf-bar"><i style={{ width: (latest && latest.companies ? Math.max(5, Math.round((latest.done / latest.companies) * 100)) : 5) + '%' }} /></div><span>Looking at {latest ? latest.done : 0} of {latest ? latest.companies : '…'} companies. You can leave this screen.</span></div></div>}
      {msg && <div className="gtiv-card gtiv-err">{msg}</div>}
      {done && <div className="gtiv-card gtob2-done"><span>{done}</span><button type="button" className="gt-btn" onClick={() => setDone('')}>OK</button></div>}
      <div className="gtob-stats">
        {card('Companies', ccs.all, view === 'companies' && f === 'all', () => go('companies', 'all'))}
        {card('People found', pcs.all, view === 'people' && f === 'all', () => go('people', 'all'))}
        {card('Ready to send to', pcs.ready, view === 'people' && f === 'ready', () => go('people', 'ready'), 'g')}
        {card('Waiting to be looked at', ccs.waiting, view === 'companies' && f === 'waiting', () => go('companies', 'waiting'))}
      </div>
      <p className="gtob-when">{latest ? <>Last looked {ukDay(latest.finished_at || latest.started_at)}{latest.credits ? ', ' + plural(latest.credits, 'Apollo credit', 'Apollo credits') + ' used' : ''}{latest.error ? '. ' + latest.error : '.'}</> : 'Not run yet. It runs after the next Monday hiring check, or run it now.'}</p>
      {pcs.mine > 0 && (
        <div className="gtiv-card gtob2-alert"><span>{plural(pcs.mine, 'email is', 'emails are')} written and waiting for you to read before {pcs.mine === 1 ? 'it goes' : 'they go'}. {pcs.mine === 1 ? 'It does' : 'They do'} not go until you send {pcs.mine === 1 ? 'it' : 'them'}, and few more are written while {pcs.mine === 1 ? 'it waits' : 'they wait'}.</span>
          <button type="button" className="gt-btn gt-btn--primary" onClick={() => navigate('/outreach/not-sent')}>Read and send</button></div>
      )}
      {ccs.check > 0 && !(view === 'companies' && f === 'check') && (
        <div className="gtiv-card gtob2-alert"><span>{plural(ccs.check, 'company has a website', 'companies have a website')} that does not look like the company's own. Nobody there is looked up until you confirm it, mark it not theirs (the AI finds the right one) or dismiss the company.</span>
          <button type="button" className="gt-btn" onClick={() => go('companies', 'check')}>Review</button></div>
      )}
      <div className="gtob2-view" role="tablist" aria-label="Show">
        <button type="button" role="tab" aria-selected={view === 'people'} className={'gtob2-vbtn' + (view === 'people' ? ' gtob2-vbtn--on' : '')} onClick={() => go('people', 'all')}>People</button>
        <button type="button" role="tab" aria-selected={view === 'companies'} className={'gtob2-vbtn' + (view === 'companies' ? ' gtob2-vbtn--on' : '')} onClick={() => go('companies', 'all')}>Companies</button>
      </div>
      <Toolbar q={q} setQ={setQ} placeholder={view === 'people' ? 'Search people, titles, emails, companies' : 'Search companies, roles, websites'} chips={view === 'people' ? peopleChips : companyChips} f={f} setF={setF} />
      {view === 'people' && (
        <BulkBar n={s.sel.size} onClear={s.clear}>
          {f === 'not'
            ? <><button type="button" className="gt-btn" disabled={busy} onClick={() => people('restore')}>Put back {s.sel.size}</button>
              <button type="button" className="gt-btn gtob2-danger" disabled={busy} onClick={() => people('clear')}>Clear {s.sel.size}</button></>
            : <button type="button" className="gt-btn gtob2-danger" disabled={busy} onClick={() => people('stop')}>Don't email {s.sel.size}</button>}
        </BulkBar>
      )}
      {view === 'companies' && (
        <BulkBar n={s.sel.size} onClear={s.clear}>
          {f === 'check' && <button type="button" className="gt-btn" disabled={busy || s.sel.size > 25} title={s.sel.size > 25 ? 'Up to 25 at a time' : ''} onClick={() => findSite(Array.from(s.sel))}>{busy ? 'Looking…' : 'Not their website ' + s.sel.size}</button>}
          {f === 'dismissed'
            ? <button type="button" className="gt-btn" disabled={busy} onClick={() => companies(Array.from(s.sel), true)}>Restore {s.sel.size}</button>
            : <button type="button" className="gt-btn gtob2-danger" disabled={busy} onClick={() => companies(Array.from(s.sel))}>Dismiss {s.sel.size}</button>}
        </BulkBar>
      )}
      {list.err && <div className="gtiv-card gtiv-err">{list.err}</div>}
      <GtTable columns={view === 'people' ? peopleCols : companyCols} rows={rows} loading={list.loading && !list.data}
        selectedIds={s.sel} onToggle={s.toggle} onToggleAll={s.toggleAll} onRowClick={(r) => setOpen({ kind: view, row: r })}
        emptyText={dq ? 'Nothing matches that search.' : view === 'people' ? 'Nobody here yet.' : 'No companies here.'} />
      <GtPager page={page} pageSize={size} total={(list.data && list.data.total) || 0} onPage={setPage} onPageSize={setSize} />

      {never && <Drawer kicker="Auto outreach" title="Never contact" onClose={() => setNever(false)}><NeverList authFetch={authFetch} /></Drawer>}
      {open && open.kind === 'people' && (() => { const p = open.row; const [t, tone] = personState(p); return (
        <Drawer kicker={p.company} title={p.full_name} onClose={() => setOpen(null)}
          foot={<button type="button" className="gt-btn" onClick={() => navigate('/clients/' + p.client_id)}>Open {p.company}</button>}>
          <Field label="Status"><Pill tone={tone}>{t}</Pill></Field>
          <Field label="Job title">{p.title || 'Not known'}</Field>
          <Field label="Work email">{p.email || 'None kept'}</Field>
          <Field label="Found by">{SOURCE[p.source] || p.source}, {ukDay(p.created_at)}</Field>
          <Field label="Hiring for">{p.role_title || '—'}</Field>
          {p.linkedin_url && <Field label="LinkedIn"><a className="gtt-link" href={p.linkedin_url} target="_blank" rel="noopener noreferrer">Open profile</a></Field>}
        </Drawer>); })()}
      {open && open.kind === 'companies' && (() => { const c = open.row; const [t, tone] = COMPANY_STATE[c.state] || ['', 'slate']; return (
        <Drawer kicker={c.role_title ? 'Hiring for ' + c.role_title : 'Company'} title={c.name} onClose={() => setOpen(null)}
          foot={<>
            {c.state === 'check' && c.website && <button type="button" className="gt-btn gt-btn--primary" disabled={busy} onClick={() => siteOk(c.client_id)}>It is their website</button>}
            {c.state === 'check' && <button type="button" className="gt-btn" disabled={busy} onClick={() => findSite([c.client_id])}>{busy ? 'Looking…' : c.website ? 'Not their website' : 'Look again'}</button>}
            {c.state === 'dismissed'
              ? <button type="button" className="gt-btn" disabled={busy} onClick={() => companies([c.client_id], true)}>Restore</button>
              : <button type="button" className="gt-btn gtob2-danger" disabled={busy} onClick={() => companies([c.client_id])}>Dismiss company</button>}
            {c.state !== 'check' && <button type="button" className="gt-btn" onClick={() => navigate('/clients/' + c.client_id)}>Open client</button>}
          </>}>
          <Field label="Status"><Pill tone={tone}>{t}</Pill></Field>
          <Field label="Website">{c.website ? <a className="gtt-link" href={siteUrl(c.website)} target="_blank" rel="noopener noreferrer">{host(c.website) || c.website} ↗</a> : 'None on the client'}</Field>
          {c.note && <div className={'gtob2-callout' + (c.state === 'check' ? ' gtob2-callout--warn' : '')}>{c.note}</div>}
          {c.state === 'check' && <p className="gtob2-note">If it is not theirs, the AI finds the right one and changes it on the client.</p>}
          {c.posting_url && <Field label="Advert"><a className="gtt-link" href={c.posting_url} target="_blank" rel="noopener noreferrer">{c.posting_title || host(c.posting_url)}</a></Field>}
          <Field label="People">{c.people || 0} found, {c.ready || 0} ready, {c.emailed || 0} emailed</Field>
          <Field label="Looked at">{c.looked_at ? ukDay(c.looked_at) : 'Not yet'}</Field>
          <CompanyPeople authFetch={authFetch} clientId={c.client_id} />
        </Drawer>); })()}
    </>
  );
}
const siteUrl = (w) => (/^https?:/i.test(w) ? w : 'https://' + w);
function CompanyPeople({ authFetch, clientId }) {
  const l = useList(authFetch, '/api/outreach/people/list', { view: 'people', client: clientId, size: 50 });
  const rows = (l.data && l.data.rows) || [];
  return (
    <div className="gtob2-sub">
      <h3>People here</h3>
      {l.loading && !l.data ? <div className="gtbx-empty">Loading…</div> : rows.length === 0 ? <p className="gtob2-note">Nobody found here yet.</p> : (
        <ul className="gtob2-mini">
          {rows.map((p) => { const [t, tone] = personState(p); return (
            <li key={p.id}><div><b>{p.full_name}</b><span>{p.title || 'Title not known'}{p.email ? ' · ' + p.email : ''}</span></div><Pill tone={tone} title={t}>{t}</Pill></li>); })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- sending figures
function useSending(authFetch) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => authFetch('/api/outreach/sending').then(setD).catch((e) => setMsg(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  return { d, msg, load };
}
function Banners({ d, navigate }) {
  return (
    <>
      {!d.live && <div className="gtiv-card gtob-warn">This copy is not production, so nothing is sent from here.</div>}
      {d.live && !d.senders && <div className="gtiv-card gtob-warn">No sending address is marked for client outreach, so nothing can go. Add one in Settings, Sending.</div>}
      {d.live && d.check && !d.check.set_up && <div className="gtiv-card gtob-stop"><span><b>Emails are not going: email checking is not set up.</b> Every address is checked before its first email goes. Add BOUNCIFY_API_KEY to the backend in Railway.</span></div>}
      {d.live && d.check && d.check.set_up && d.check.error && <div className="gtiv-card gtob-stop"><span><b>Emails are waiting: the email check is not answering.</b> {d.check.error}. It tries again every 10 minutes. If it says no credits, top up Bouncify.</span></div>}
      {d.live && !d.send_on && d.pause && <div className="gtiv-card gtob-stop"><span><b>{d.pause.kind === 'spam' ? 'Paused: spam complaints' : 'Paused: bounces'}</b>{d.pause.why}. Look at who it was, then switch Sending outreach emails back on in Settings, Automations.</span>{navigate && <button type="button" className="gt-btn" onClick={() => navigate('/settings')}>Open Settings</button>}</div>}
    </>
  );
}
function SendStats({ d, navigate }) {
  return (
    <>
      <div className="gtob-stats">
        <div className="gtob-stat"><em>Sent today</em><b>{d.today} <small>of {d.cap}</small></b></div>
        <button type="button" className="gtob-stat gtob-stat--go" onClick={() => navigate('/outreach/not-sent')}><em>Waiting for you</em><b>{d.mine}</b></button>
        <button type="button" className="gtob-stat gtob-stat--go" onClick={() => navigate('/outreach/people')}><em>People ready</em><b>{d.waiting}</b></button>
        <div className="gtob-stat"><em>Sending now</em><b className="gtob-small">{!d.live ? 'Not from this copy' : !d.send_on ? 'Switched off' : !d.senders ? 'No sending address' : d.check && (!d.check.set_up || d.check.error) ? 'Waiting for the email check' : 'Yes, one every 2 to 7 min'}</b></div>
      </div>
      <p className="gtob-when">Around the clock, every day, one email at a time, 2 to 7 minutes apart. The daily cap starts at 20 and rises by 10 a week to 50 while the sending domain is new. One follow-up 5 days later, unless anyone at the company replies. Every address is checked as soon as the person is found, and the result shows on People and Not sent: a bad address is never sent to. Catch-all, shared inbox and could-not-check go when you press Send; sent by itself, one waits for you{d.check && d.check.checked && d.check.checked.n ? ' (' + d.check.checked.n + ' checked so far, ' + d.check.checked.bad + ' did not exist)' : ''}.</p>
    </>
  );
}
function sentState(m) {
  if (m.send_status === 'replied') return ['Replied', 'green'];
  if (m.last_event === 'bounced') return ['Bounced', 'rose'];
  if (m.last_event === 'complained') return ['Marked as spam', 'rose'];
  if (m.send_status === 'unsubscribed') return ['Unsubscribed', 'amber'];
  if (m.send_status === 'stopped' && m.stop_reason) return [m.stop_reason === 'Someone at the company replied' ? 'Colleague replied' : m.stop_reason, 'amber'];
  if (m.clicks > 0) return ['Clicked', 'green'];
  if (m.last_event === 'delivered') return ['Delivered', 'teal'];
  return ['Sent', 'slate'];
}
const opened = (m) => (m.clicked_links || []).map((l) => (l === 'article' ? 'the article' : 'the role page')).join(' and ');

// ---------------------------------------------------------------- Sent
function Sent({ authFetch, navigate }) {
  const ov = useSending(authFetch);
  const [f, setF] = useState('all');
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim());
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const list = useList(authFetch, '/api/outreach/sending/list', { tab: 'sent', f, q: dq, page, size });
  useEffect(() => { setPage(0); }, [f, dq, size]);
  const counts = (list.data && list.data.counts) || {};
  const sentRows = (list.data && list.data.rows) || [];
  const s = useSelection(sentRows);
  const [done, setDone] = useState('');
  async function stopFollow() {
    const ids = Array.from(new Set(sentRows.filter((r) => s.sel.has(r.id)).map((r) => r.person_id)));
    if (!ids.length) return;
    setBusy(true); setMsg(''); setDone('');
    try {
      const r = await authFetch('/api/outreach/people/bulk', { method: 'POST', body: JSON.stringify({ ids, action: 'stop' }) });
      setDone(r.done ? 'Follow-up stopped for ' + plural(r.done, 'person', 'people') + '.' + (r.done < r.asked ? ' The rest had already finished, replied or bounced.' : '') : 'Nothing to stop: they had already finished, replied or bounced.');
      s.clear(); list.reload(); ov.load();
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  }
  async function replied(id) {
    setBusy(true); setMsg('');
    try { await authFetch('/api/outreach/sending/' + id + '/replied', { method: 'POST' }); setOpen(null); list.reload(); ov.load(); } catch (e) { setMsg(e.message); }
    setBusy(false);
  }
  if (!ov.d) return ov.msg ? <div className="gtiv-card gtiv-err">{ov.msg}</div> : <div className="gtiv-card gtiv-loading">Loading…</div>;
  const cols = [
    { key: 'person', label: 'Person', width: 220, cell: (m) => <Two a={m.full_name} b={(m.title ? m.title + ', ' : '') + m.company} /> },
    { key: 'subject', label: 'Email', width: 290, cell: (m) => <Two a={m.subject} b={(m.step > 1 ? 'Follow-up' : 'First email') + (m.role_title ? ' · ' + m.role_title : '')} /> },
    { key: 'sent', label: 'Sent', width: 120, cell: (m) => <span className="gtt-date">{when(m.sent_at)}</span> },
    { key: 'status', label: 'Status', width: 150, cell: (m) => { const [t, tone] = sentState(m); return <Pill tone={tone}>{t}</Pill>; } },
    { key: 'clicks', label: 'Clicks', width: 160, cell: (m) => (m.clicks > 0 ? <span className="gtt-ctext" title={'Opened ' + opened(m)}>{m.clicks} · {opened(m)}</span> : <Dash />) },
  ];
  const chips = [{ key: 'all', label: 'All', n: counts.all }, { key: 'clicked', label: 'Clicked', n: counts.clicked }, { key: 'replied', label: 'Replied', n: counts.replied }, { key: 'followups', label: 'Follow-ups', n: counts.followups }, { key: 'problems', label: 'Bounced or unsubscribed', n: counts.problems, warn: true }];
  return (
    <>
      <Banners d={ov.d} navigate={navigate} />
      <SendStats d={ov.d} navigate={navigate} />
      {msg && <div className="gtiv-card gtiv-err">{msg}</div>}
      <Toolbar q={q} setQ={setQ} placeholder="Search people, companies, subjects" chips={chips} f={f} setF={setF} />
      {list.err && <div className="gtiv-card gtiv-err">{list.err}</div>}
      {done && <div className="gtiv-card gtob2-done"><span>{done}</span><button type="button" className="gt-btn" onClick={() => setDone('')}>OK</button></div>}
      <BulkBar n={s.sel.size} onClear={s.clear}>
        <button type="button" className="gt-btn gtob2-danger" disabled={busy} onClick={stopFollow}>Stop follow-up {s.sel.size}</button>
      </BulkBar>
      <GtTable columns={cols} rows={sentRows} loading={list.loading && !list.data} selectedIds={s.sel} onToggle={s.toggle} onToggleAll={s.toggleAll} onRowClick={(m) => setOpen(m)}
        emptyText={dq ? 'Nothing matches that search.' : f === 'all' ? 'Nothing sent yet.' : 'None of these yet.'} />
      <GtPager page={page} pageSize={size} total={(list.data && list.data.total) || 0} onPage={setPage} onPageSize={setSize} />
      {open && (() => { const m = open; const [t, tone] = sentState(m); return (
        <Drawer kicker={(m.step > 1 ? 'Follow-up' : 'First email') + ' · ' + when(m.sent_at)} title={m.full_name + ', ' + m.company} onClose={() => setOpen(null)}
          foot={<>
            {m.send_status !== 'replied' && <button type="button" className="gt-btn" disabled={busy} title="A reply that only reached your own inbox: stops the follow-up and everyone at the company" onClick={() => replied(m.id)}>They replied to me</button>}
            <button type="button" className="gt-btn" onClick={() => navigate('/clients/' + m.client_id)}>Open {m.company}</button>
          </>}>
          <Field label="Status"><Pill tone={tone}>{t}</Pill></Field>
          <Field label="To">{m.to_email}</Field>
          <Field label="From">{m.from_email}</Field>
          {m.clicks > 0 && <Field label="Clicks">Opened {opened(m)} {when(m.first_click_at)}{m.clicks > 1 ? ', ' + m.clicks + ' clicks, last ' + when(m.last_click_at) : ''}</Field>}
          <div className="gtob2-mail"><b>{m.subject}</b><pre>{bodyText(m.body_text)}</pre></div>
        </Drawer>); })()}
    </>
  );
}

// ---------------------------------------------------------------- Not sent
function NotSent({ authFetch, navigate }) {
  const ov = useSending(authFetch);
  const [view, setView] = useState('held'); // held | notemailed
  const [f, setF] = useState('all');
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim());
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [note, setNote] = useState('');
  const list = useList(authFetch, '/api/outreach/sending/list', { tab: view, f: view === 'held' ? f : 'all', q: dq, page, size });
  useEffect(() => { setPage(0); }, [view, f, dq, size]);
  const rows = (list.data && list.data.rows) || [];
  const s = useSelection(rows);
  useEffect(() => { s.clear(); setF('all'); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => { list.reload(); ov.load(); };
  // Jimmy's own edit of the open email: saved by Save, and saved first when he presses Send or Send me a test.
  const [draft, setDraft] = useState(null);
  useEffect(() => { setDraft(open && open.body_text ? { id: open.id, subject: open.subject || '', html: bodyToHtml(open.body_text), dirty: false } : null); }, [open && open.id]); // eslint-disable-line react-hooks/exhaustive-deps
  async function saveDraft() {
    if (!draft || !draft.dirty) return true;
    try {
      const r = await authFetch('/api/outreach/sending/' + draft.id + '/edit', { method: 'POST', body: JSON.stringify({ subject: draft.subject, body: htmlToBody(draft.html) }) });
      setDraft((d) => (d && d.id === r.id ? { ...d, dirty: false } : d));
      setOpen((o) => (o && o.id === r.id ? { ...o, subject: r.subject, body_text: r.body_text, flags: r.flags, status: r.status } : o));
      list.reload();
      return true;
    } catch (e) { setMsg(e.message); return false; }
  }
  async function act(id, a) {
    setBusy(id + a); setMsg(''); setNote('');
    if ((a === 'send') && draft && draft.id === id && !(await saveDraft())) { setBusy(''); return; }
    try { await authFetch('/api/outreach/sending/' + id + '/' + a, { method: 'POST' }); s.setSel((p) => { const n = new Set(p); n.delete(id); return n; }); setOpen(null); refresh(); }
    catch (e) { setMsg(e.message); }
    setBusy('');
  }
  async function bulk(a) {
    const ids = rows.filter((r) => s.sel.has(r.id)).map((r) => r.id);
    if (!ids.length) return;
    setBusy('bulk' + a); setMsg(''); setNote('');
    try {
      const out = await authFetch('/api/outreach/sending/bulk', { method: 'POST', body: JSON.stringify({ ids, action: a }) });
      const b = out.bulk || {};
      const parts = [];
      if (b.sent) parts.push(plural(b.sent, 'email', 'emails') + ' sent');
      if (b.queued) parts.push(plural(b.queued, 'email', 'emails') + ' queued: they go one at a time, 2 to 7 minutes apart');
      if (b.skipped) parts.push(plural(b.skipped, 'email', 'emails') + ' not sent');
      if (b.stopped) parts.push(plural(b.stopped, 'email', 'emails') + ' stopped by the last check');
      if (b.failed) parts.push(plural(b.failed, 'email', 'emails') + ' failed to send');
      setNote((parts.length ? parts.join('. ') + '.' : 'Nothing was done.') + (b.problems && b.problems.length ? ' ' + b.problems.join(' ') : ''));
      s.clear(); refresh();
    } catch (e) { setMsg(e.message); }
    setBusy('');
  }
  // Send anyway: the people Jimmy picks go on the send list, whatever the rules said. Not anyone without an email.
  async function sendAnyway(ids) {
    const pick = rows.filter((r) => ids.includes(r.id) && r.email).map((r) => r.id);
    const noEmail = ids.length - pick.length;
    if (!pick.length) { setMsg('None of these have an email address.'); return; }
    setBusy('force'); setMsg(''); setNote('');
    try {
      const r = await authFetch('/api/outreach/people/bulk', { method: 'POST', body: JSON.stringify({ ids: pick, action: 'force' }) });
      setNote(plural(r.done, 'person', 'people') + ' will be written to and sent, one every 2 to 7 minutes.' + (noEmail ? ' ' + plural(noEmail, 'had', 'had') + ' no email address.' : '') + (r.done < pick.length ? ' ' + (pick.length - r.done) + ' could not be: on your Never contact list or already emailed.' : ''));
      s.clear(); setOpen(null); refresh();
    } catch (e) { setMsg(e.message); }
    setBusy('');
  }
  async function clear(ids) {
    setBusy('clear'); setMsg(''); setNote('');
    try {
      if (ids === 'all') await authFetch('/api/outreach/not-emailed/dismiss', { method: 'POST' });
      else for (const id of ids) await authFetch('/api/outreach/not-emailed/' + id + '/dismiss', { method: 'POST' }); // eslint-disable-line no-await-in-loop
      s.clear(); setOpen(null); refresh();
    } catch (e) { setMsg(e.message); }
    setBusy('');
  }
  async function test(id) {
    setBusy(id + 'test'); setMsg(''); setNote('');
    if (draft && draft.id === id && !(await saveDraft())) { setBusy(''); return; } // the test shows the email as edited
    try { const r = await authFetch('/api/outreach/sending/' + id + '/test', { method: 'POST' }); setNote('Test sent to ' + r.to + '. Its follow-up comes in about a minute, in the same thread. Reply to either to check the reply reaches the platform; the bell will say so.'); }
    catch (e) { setMsg(e.message); }
    setBusy('');
  }
  if (!ov.d) return ov.msg ? <div className="gtiv-card gtiv-err">{ov.msg}</div> : <div className="gtiv-card gtiv-loading">Loading…</div>;
  const counts = (list.data && list.data.counts) || {};
  const CHECK_SHORT = { catch_all: 'Catch-all', shared_inbox: 'Shared inbox', unknown: 'Could not check' };
  const heldState = (m) => {
    if (m.status === 'failed') return /^Writing failed/.test(m.error || '') ? ['Writing failed', 'rose'] : ['Send failed', 'rose'];
    const un = (m.flags || []).find((x) => x.code === 'unverified');
    if (un) return [CHECK_SHORT[un.check || m.check_kind] || 'Email not confirmed', 'amber'];
    return (m.flags || []).some((x) => x.code === 'first') ? ['Read before it goes', 'amber'] : ['Needs you', 'amber'];
  };
  const heldCols = [
    { key: 'person', label: 'Person', width: 240, cell: (m) => <Two a={m.full_name} b={(m.title ? m.title + ', ' : '') + m.company} /> },
    { key: 'subject', label: 'Email', width: 300, cell: (m) => <Two a={m.subject || (m.step > 1 ? 'Follow-up' : 'First email')} b={m.status === 'failed' ? m.error : (m.flags || []).map((x) => x.text).join(' · ')} /> },
    { key: 'check', label: 'Email check', width: 130, cell: (m) => <CheckPill kind={m.check_kind} email={m.to_email} /> },
    { key: 'status', label: 'Status', width: 170, cell: (m) => { const [t, tone] = heldState(m); return <Pill tone={tone}>{t}</Pill>; } },
    { key: 'written', label: 'Written', width: 130, cell: (m) => <span className="gtt-date">{when(m.created_at)}</span> },
  ];
  const notCols = [
    { key: 'person', label: 'Person', width: 240, cell: (p) => <Two a={p.full_name} b={p.title || 'Title not known'} /> },
    { key: 'company', label: 'Company', width: 200, cell: (p) => <Two a={p.company} b={p.role_title} /> },
    { key: 'email', label: 'Email', width: 220, cell: (p) => (p.email ? <span className="gtt-ctext">{p.email}</span> : <Dash />) },
    { key: 'check', label: 'Email check', width: 130, cell: (p) => <CheckPill kind={p.check_kind} email={p.email} /> },
    { key: 'why', label: 'Why not', width: 250, cell: (p) => <Pill tone="amber" title={p.stop_reason || p.reason || ''}>{p.stop_reason || p.reason || 'Not a prospect any more'}</Pill> },
  ];
  const isHeld = view === 'held';
  return (
    <>
      <Banners d={ov.d} navigate={navigate} />
      <div className="gtob2-view" role="tablist" aria-label="Show">
        <button type="button" role="tab" aria-selected={isHeld} className={'gtob2-vbtn' + (isHeld ? ' gtob2-vbtn--on' : '')} onClick={() => setView('held')}>Waiting for you <b>{ov.d.held_count}</b></button>
        <button type="button" role="tab" aria-selected={!isHeld} className={'gtob2-vbtn' + (!isHeld ? ' gtob2-vbtn--on' : '')} onClick={() => setView('notemailed')}>Not emailed <b>{ov.d.not_emailed_count}</b></button>
      </div>
      {note && <div className="gtiv-card gtob-note">{note}</div>}
      {msg && <div className="gtiv-card gtiv-err">{msg}</div>}
      <Toolbar q={q} setQ={setQ} placeholder="Search people, companies, subjects"
        chips={isHeld ? [{ key: 'all', label: 'All', n: counts.all }, { key: 'mine', label: 'Waiting for you', n: counts.mine }, { key: 'catch_all', label: 'Catch-all', n: counts.catch_all }, { key: 'shared_inbox', label: 'Shared inbox', n: counts.shared_inbox }, { key: 'unknown', label: 'Could not check', n: counts.unknown }, { key: 'failed', label: 'Failed', n: counts.failed, warn: true }] : null} f={f} setF={setF}
        right={!isHeld && ov.d.not_emailed_count > 0 ? <button type="button" className="gt-btn" disabled={!!busy} onClick={() => clear('all')}>Clear them all</button> : null} />
      <BulkBar n={s.sel.size} onClear={s.clear}>
        {isHeld ? <>
          <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy} onClick={() => bulk('send')}>{busy === 'bulksend' ? 'Sending…' : 'Send ' + s.sel.size}</button>
          <button type="button" className="gt-btn gtob2-danger" disabled={!!busy} onClick={() => bulk('skip')}>{busy === 'bulkskip' ? 'Working…' : "Don't send " + s.sel.size}</button>
        </> : <>
          <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy || !rows.some((r) => s.sel.has(r.id) && r.email)} onClick={() => sendAnyway(Array.from(s.sel))}>{busy === 'force' ? 'Sending…' : 'Send anyway ' + rows.filter((r) => s.sel.has(r.id) && r.email).length}</button>
          <button type="button" className="gt-btn" disabled={!!busy} onClick={() => clear(Array.from(s.sel))}>Clear {s.sel.size}</button>
        </>}
      </BulkBar>
      {list.err && <div className="gtiv-card gtiv-err">{list.err}</div>}
      <GtTable columns={isHeld ? heldCols : notCols} rows={rows} loading={list.loading && !list.data} selectedIds={s.sel} onToggle={s.toggle} onToggleAll={s.toggleAll}
        onRowClick={(r) => setOpen(r)} emptyText={dq ? 'Nothing matches that search.' : isHeld ? 'Nothing is waiting. Clean emails go by themselves.' : 'Nobody here.'} />
      <GtPager page={page} pageSize={size} total={(list.data && list.data.total) || 0} onPage={setPage} onPageSize={setSize} />
      {open && isHeld && (() => { const m = open; const [t, tone] = heldState(m); const writingFailed = /^Writing failed/.test(m.error || ''); return (
        <Drawer wide kicker={(m.step > 1 ? 'Follow-up' : 'First email') + (m.role_title ? ' · ' + m.role_title : '')} title={m.full_name + ', ' + m.company} onClose={async () => { if (await saveDraft()) setOpen(null); }}
          foot={<>
            {m.status === 'needs_you' && <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy} onClick={() => act(m.id, 'send')}>Send</button>}
            {m.status === 'failed' && !writingFailed && <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy} title="It cannot go twice" onClick={() => act(m.id, 'send')}>Try sending again</button>}
            {(m.status === 'needs_you' || writingFailed) && <button type="button" className="gt-btn" disabled={!!busy} onClick={() => act(m.id, 'rewrite')}>{busy === m.id + 'rewrite' ? 'Writing…' : 'Write again'}</button>}
            {m.step === 1 && m.body_text && <button type="button" className="gt-btn" disabled={!!busy} title="Sends this email to your own inbox, then its follow-up a minute later. The prospect gets nothing." onClick={() => test(m.id)}>{busy === m.id + 'test' ? 'Sending…' : 'Send me a test'}</button>}
            <button type="button" className="gt-btn gtob2-danger" disabled={!!busy} onClick={() => act(m.id, 'skip')}>Don't send</button>
          </>}>
          <Field label="Status"><Pill tone={tone}>{t}</Pill></Field>
          {(m.flags || []).length > 0 && <Field label="Why it waits">{m.flags.map((x) => x.text).join('. ')}</Field>}
          {m.status === 'failed' && <div className="gtob2-callout gtob2-callout--warn">{m.error}</div>}
          <Field label="To">{m.to_email}</Field>
          {draft && draft.id === m.id && m.status === 'needs_you' ? (
            <div className="gtob2-edit">
              {m.step === 1
                ? <label className="gtob2-edit-sub"><span>Subject</span><input className="gtiv-in" value={draft.subject} maxLength={120} onChange={(e) => { const v = e.target.value; setDraft((d) => ({ ...d, subject: v, dirty: true })); }} /></label>
                : <p className="gtob2-note">A follow-up keeps the first email's subject, in the same thread.</p>}
              <GtEditor value={draft.html} minHeight={360} onChange={(h) => setDraft((d) => (d && h !== d.html ? { ...d, html: h, dirty: d.dirty || htmlToBody(h) !== htmlToBody(d.html) } : d))} />
              <p className="gtob2-note">The Article and Library page links stay as links. The email goes as plain paragraphs, so bold, headings and tables are not kept.</p>
              {draft.dirty && <div className="gtob2-edit-bar"><span>Changes not saved yet. Send saves them first.</span><button type="button" className="gt-btn" disabled={!!busy} onClick={async () => { setBusy('save'); await saveDraft(); setBusy(''); }}>{busy === 'save' ? 'Saving…' : 'Save changes'}</button></div>}
            </div>
          ) : <div className="gtob2-mail"><b>{m.subject || (m.step > 1 ? 'Re: the first email' : '')}</b><pre>{bodyText(m.body_text) || 'Nothing written.'}</pre></div>}
        </Drawer>); })()}
      {open && !isHeld && (() => { const p = open; return (
        <Drawer kicker={p.company} title={p.full_name} onClose={() => setOpen(null)}
          foot={<>
            {p.email && <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy} onClick={() => sendAnyway([p.id])}>Send anyway</button>}
            <button type="button" className="gt-btn" disabled={!!busy} onClick={() => clear([p.id])}>Clear from this list</button>
            <button type="button" className="gt-btn" onClick={() => navigate('/clients/' + p.client_id)}>Open {p.company}</button>
          </>}>
          <Field label="Why not">{p.stop_reason || p.reason || 'Not a prospect any more'}</Field>
          <Field label="Job title">{p.title || 'Not known'}</Field>
          <Field label="Email">{p.email || 'None kept'}</Field>
          <Field label="Hiring for">{p.role_title || '—'}</Field>
        </Drawer>); })()}
    </>
  );
}

export default function GtOutreach({ getToken }) {
  const authFetch = useAuthFetch(getToken);
  const navigate = useNavigate();
  const { tab } = useParams();
  const t = ['people', 'sent', 'not-sent'].includes(tab) ? tab : 'hiring';
  const sw = (key, path, label, sub) => (
    <button type="button" role="tab" aria-selected={t === key} className={'gtlx-sw' + (t === key ? ' gtlx-sw--on' : '')} style={{ paddingLeft: 14 }} onClick={() => navigate(path)}>{label}<em>{sub}</em></button>);
  return (
    <div className="gtc-wrap gtob">
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">Auto outreach</h1>
          <p className="gtc-sub">Companies hiring for roles we have written about, found every week, the people there, and the article sent to them by itself.</p>
        </div>
      </div>
      <div className="gtlx-switch" role="tablist" aria-label="Auto outreach">
        {sw('hiring', '/outreach', 'Hiring now', 'from the weekly check')}
        {sw('people', '/outreach/people', 'People', 'who to send to')}
        {sw('sent', '/outreach/sent', 'Sent', 'emails that went')}
        {sw('not-sent', '/outreach/not-sent', 'Not sent', 'and why')}
      </div>
      {t === 'people' ? <People authFetch={authFetch} navigate={navigate} /> : t === 'sent' ? <Sent authFetch={authFetch} navigate={navigate} /> : t === 'not-sent' ? <NotSent authFetch={authFetch} navigate={navigate} /> : <HiringNow authFetch={authFetch} navigate={navigate} />}
    </div>
  );
}
